import React, { useEffect, useState } from 'react';
import { getPersonalDataTableSubmit } from '~/store/processing-personal/thunks/personalDataTableThunks.ts';
import { formatDateToISO } from '~/helpers/utils.ts';
import { getSubmitColumns } from './getSubmitColumns.tsx';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { TSubmitRecord, TTableFilterPayload, TTableFilters, TTableSorter } from '../types.ts';
import { useDocumentClick } from '../hooks/useDocumentClick.ts';
import { PersonalDataMuiTable } from '../components/PersonalDataMuiTable.tsx';
import { NavigationButtons } from '../components/NavigationButtons.tsx';
import { sortByActiveSort, TServerSort, TServerSortDirection } from '../utils/sorting.ts';
import { buildTablePayload } from '../utils/utils.ts';
import { TPageConfiguration } from '../../types.ts';
import { SUBMIT_SORT_FIELD_MAP } from '../constants.ts';

interface IPersonalDataTableSubmitProps {
  content: TSubmitRecord[];
  pageConfiguration?: TPageConfiguration;
  onLoading: (loading: boolean) => void;
  BoxSVG: React.ComponentType;
}

export default function PersonalDataTableSubmit({
  content,
  pageConfiguration,
  onLoading,
  BoxSVG,
}: IPersonalDataTableSubmitProps) {
  const dispatch = useAppDispatch();

  const [contentSubmit, setContentSubmit] = useState<TSubmitRecord[]>(
    Array.isArray(content) ? content : []
  );
  const [currentPage, setCurrentPage] = useState<number>(pageConfiguration?.page ?? 0);
  const [totalElements, setTotalElements] = useState<number>(pageConfiguration?.totalElements ?? 0);
  const [currentFilters, setCurrentFilters] = useState<TTableFilterPayload>({});
  const [currentSort, setCurrentSort] = useState<TServerSort>(null);

  const pageSize = 50;
  const handleDocumentClick = useDocumentClick('submit');

  const columns = getSubmitColumns({
    content: contentSubmit || [],
    handleDocumentClick,
    onCheckboxChange: () => {},
  });

  const fetchData = (
    page: number,
    filters: TTableFilterPayload = currentFilters,
    sort: TServerSort = currentSort
  ) => {
    onLoading(true);

    const payload = buildTablePayload(page, pageSize, filters, sort);

    dispatch(getPersonalDataTableSubmit({ data: payload }))
      .unwrap()
      .then((res) => {
        onLoading(false);
        if (res) {
          const applications = Array.isArray(res?.applications)
            ? (res.applications as TSubmitRecord[])
            : [];
          const sortedApplications = sortByActiveSort(applications, sort);

          setContentSubmit(sortedApplications);
          setTotalElements(
            Number(res?.pageConfiguration?.totalElements ?? sortedApplications.length)
          );
        }
      })
      .catch(() => {
        onLoading(false);
      });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchData(newPage, currentFilters, currentSort);
  };

  const onChange = (
    filters: TTableFilters,
    sorter: TTableSorter | TTableSorter[],
    extra: { action: string }
  ) => {
    const isSortingChange = extra.action === 'sort';

    if (isSortingChange) {
      if (!Array.isArray(sorter) && sorter.field && sorter.order) {
        const apiSortField = SUBMIT_SORT_FIELD_MAP[sorter.field];
        const direction: TServerSortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';

        if (apiSortField) {
          const nextSort: TServerSort = { field: apiSortField, direction };
          setCurrentSort(nextSort);
          setCurrentPage(0);
          fetchData(0, currentFilters, nextSort);
        }
      } else {
        setCurrentSort(null);
        setCurrentPage(0);
        fetchData(0, currentFilters, null);
      }
      return;
    }

    const getDateRange = (prefix: string, date?: string) => {
      if (!date) return {};

      return {
        [`${prefix}After`]: formatDateToISO(date, 'start'),
        [`${prefix}Before`]: formatDateToISO(date, 'end'),
      };
    };

    const payload = {
      email: filters.email?.[0] || null,
      phone: filters.phone?.[0] || null,
      identificationNumber: filters.identificationNumber?.[0] || null,
      denyType: filters.denyType?.[0] || null,
      status: filters.status?.[0] || null,
      initiatedByFullName: filters.initiatedByFullName?.[0] || null,
      ...getDateRange('createdAt', filters.createdAt?.[0] || ''),
      ...getDateRange('updatedAt', filters.updatedAt?.[0] || ''),
    };

    const cleanedPayload = Object.entries(payload).reduce<TTableFilterPayload>(
      (acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    setCurrentFilters(cleanedPayload);
    setCurrentPage(0);
    fetchData(0, cleanedPayload);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    if (Array.isArray(content)) {
      const sortedContent = sortByActiveSort(content, currentSort);
      setContentSubmit(sortedContent);

      if (!pageConfiguration) {
        setTotalElements(sortedContent.length);
      }
    }

    if (pageConfiguration) {
      setTotalElements(pageConfiguration.totalElements);
      setCurrentPage(pageConfiguration.page);
    }
  }, [content, currentSort, pageConfiguration]);

  return (
    <>
      <PersonalDataMuiTable
        rowKey="id"
        onChange={onChange}
        columns={columns}
        dataSource={contentSubmit}
        locale={{
          emptyText: <BoxSVG />,
        }}
      />
      <NavigationButtons
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={totalElements}
      />
    </>
  );
}
